import { useAuth } from "./AuthContext";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import LoadingSpinner from "./pages/Loader";
import { useEffect, useState, useRef } from "react";
import "./AutoLogoutModal.css";

const AutoLogoutModal = ({ countdown, onExtendSession }) => {
  return (
    <div className="auto-logout-modal-overlay">
      <div className="auto-logout-modal">
        <h2>Session Timeout Warning</h2>
        <p>You will be automatically logged out due to inactivity in:</p>
        <div className="countdown-timer">{countdown} seconds</div>
        <p>Click "Stay Logged In" to continue your session.</p>
        <div className="modal-actions">
          <button onClick={onExtendSession} className="btn btn-primary">
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ requiredRole }) => {
  const { isAuthenticated, userRole, isLoading, logout } = useAuth();
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);
  
  // Use refs for timers to avoid dependency issues
  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // Activity tracking - reset timers without hiding warning
  const resetTimers = () => {
    console.log("Resetting timers...");
    
    // Clear existing timers but DON'T hide the warning if it's showing
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Only reset countdown and hide warning if we're not currently showing the modal
    if (!showWarning) {
      setCountdown(30);
    }

    // Set new timers only if user is authenticated
    if (isAuthenticated) {
      const warningTime = 14.5 * 60 * 1000; // 4.5 minutes (270,000 ms)
      const logoutTime = 15 * 60 * 1000; // 5 minutes (300,000 ms)

      console.log(`Setting warning timer for ${warningTime/1000/60} minutes`);
      console.log(`Setting logout timer for ${logoutTime/1000/60} minutes`);

      // Set warning timer
      warningTimerRef.current = setTimeout(() => {
        console.log("Warning timer triggered - showing popup");
        setShowWarning(true);
        setCountdown(30);
        
        // Start countdown
        countdownTimerRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              console.log("Countdown finished - logging out");
              clearInterval(countdownTimerRef.current);
              handleLogout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

      }, warningTime);

      // Set logout timer
      logoutTimerRef.current = setTimeout(() => {
        console.log("Logout timer triggered");
        handleLogout();
      }, logoutTime);
    }
  };

  // Separate function to handle user activity when warning is shown
  const handleUserActivity = () => {
    console.log("User activity detected");
    
    if (showWarning) {
      // If warning is showing, user activity should NOT reset timers
      // Only hide the warning and reset countdown, but keep the original logout timer
      console.log("Warning is showing - only hiding modal, not resetting main timers");
      setShowWarning(false);
      setCountdown(30);
      
      // Clear the countdown timer but NOT the main logout timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    } else {
      // If no warning is showing, reset all timers normally
      resetTimers();
    }
  };

  const handleExtendSession = () => {
    console.log("User extended session");
    setShowWarning(false);
    setCountdown(30);
    
    // Clear all timers and reset properly
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Now reset all timers including the main logout timer
    resetTimers();
  };

  const handleLogout = () => {
    console.log("Auto-logout triggered");
    // Save current path before logging out
    const currentPath = location.pathname + location.search;
    localStorage.setItem('returnUrl', currentPath);
    console.log("Saved return URL:", currentPath);
    
    // Clear all timers
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Logout user
    logout();
  };

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log("Setting up activity listeners");
    
    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    
    const resetActivity = () => {
      handleUserActivity();
    };

    activities.forEach(activity => {
      document.addEventListener(activity, resetActivity, { passive: true });
    });

    // Initialize timers
    resetTimers();

    return () => {
      console.log("Cleaning up activity listeners and timers");
      // Cleanup event listeners
      activities.forEach(activity => {
        document.removeEventListener(activity, resetActivity);
      });
      
      // Cleanup timers
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [isAuthenticated, showWarning]); // Added showWarning to dependency

  // Check current time in CAT timezone (UTC+2)
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentTimeInMinutes = hours * 60 + minutes;
  const startRestrictionTime = 17 * 60; // 5:00 PM (17:00)
  const endRestrictionTime = 8 * 60 + 30; // 8:30 AM (08:30)

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  // Restrict access from 5:00 PM to 8:30 AM for non-superAdmin users
  const isRestrictedTime =
    currentTimeInMinutes >= startRestrictionTime ||
    currentTimeInMinutes < endRestrictionTime;
  if (isRestrictedTime && userRole !== "supperAdmin" && userRole !== "ceo" && userRole !== "coo" && userRole !== "receptionist") {
    return <Navigate to="/restricted-time" replace />;
  }

  if (!isAuthenticated) {
    // Save the attempted URL for redirect after login
    const returnUrl = location.pathname + location.search;
    localStorage.setItem('returnUrl', returnUrl);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Handle both single role (string) and multiple roles (array)
  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole];

    if (!allowedRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return (
    <>
      {showWarning && (
        <AutoLogoutModal 
          countdown={countdown}
          onExtendSession={handleExtendSession}
        />
      )}
      <Outlet />
    </>
  );
};

export default ProtectedRoute;